import { useEffect, useMemo, useState } from "react";
import { getAllTransactions } from "./TransactionService";
import { getAllCategories } from "../categories/CategoryService";
import { getBudgets, saveBudget } from "./BudgetService";
import { formatCurrency, monthLabel, recentMonthOptions, transactionMonth, normalizeTransactionType } from "./transactionUtils";
import "./BudgetDashboard.css";

export default function BudgetDashboard({ refreshSignal }) {
  const monthOptions = useMemo(() => recentMonthOptions(8), []);
  const [month, setMonth] = useState(monthOptions[0]?.value || "");
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState({});

  useEffect(() => {
    load();
  }, [month, refreshSignal]);

  const load = async () => {
    const [transactionData, categoryData, budgetData] = await Promise.all([
      getAllTransactions(),
      getAllCategories(),
      getBudgets(month),
    ]);
    setTransactions(Array.isArray(transactionData) ? transactionData : []);
    setCategories(Array.isArray(categoryData) ? categoryData : []);
    setBudgets(
      Object.fromEntries((Array.isArray(budgetData) ? budgetData : []).map((item) => [item.categoryId, Number(item.budgetAmount || 0)]))
    );
  };

  const expenseCategories = useMemo(
    () => categories.filter((category) => String(category.categoryType || "").toLowerCase() !== "income"),
    [categories]
  );

  const spendingByCategory = useMemo(() => {
    const spending = {};
    transactions.forEach((transaction) => {
      if (transactionMonth(transaction) !== month) return;
      if (normalizeTransactionType(transaction.transactionType) !== "EXPENSE") return;
      const key = transaction.categoryId || "uncategorized";
      spending[key] = (spending[key] || 0) + Number(transaction.transactionAmount || 0);
    });
    return spending;
  }, [month, transactions]);

  const rows = useMemo(() => {
    return expenseCategories
      .map((category) => {
        const budget = Number(budgets?.[category.id] || 0);
        const actual = Number(spendingByCategory[category.id] || 0);
        return {
          id: category.id,
          name: category.categoryName,
          budget,
          actual,
          remaining: budget - actual,
          progress: budget > 0 ? Math.min((actual / budget) * 100, 100) : 0,
        };
      })
      .sort((left, right) => right.actual - left.actual || left.name.localeCompare(right.name));
  }, [budgets, expenseCategories, month, spendingByCategory]);

  const totals = useMemo(() => {
    return rows.reduce(
      (summary, row) => {
        summary.budget += row.budget;
        summary.actual += row.actual;
        return summary;
      },
      { budget: 0, actual: 0 }
    );
  }, [rows]);

  const updateBudget = async (categoryId, value) => {
    const amount = Number(value || 0);
    const res = await saveBudget({
      categoryId,
      budgetMonth: month,
      budgetAmount: amount,
    });
    if (!res.ok) return;
    setBudgets((current) => ({ ...current, [categoryId]: amount }));
  };

  return (
    <div className="budget-dashboard">
      <div className="budget-dashboard-header">
        <div>
          <h2>Budgets</h2>
          <p>Budgets are stored on the server and compared against recorded expense transactions.</p>
        </div>
        <select className="input" value={month} onChange={(e) => setMonth(e.target.value)}>
          {monthOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="budget-dashboard-summary">
        <BudgetMetric label="Month" value={monthLabel(month)} />
        <BudgetMetric label="Budgeted" value={formatCurrency(totals.budget)} />
        <BudgetMetric label="Spent" value={formatCurrency(totals.actual)} />
        <BudgetMetric label="Remaining" value={formatCurrency(totals.budget - totals.actual)} tone={(totals.budget - totals.actual) < 0 ? "negative" : "positive"} />
      </div>

      <div className="budget-dashboard-table">
        {rows.map((row) => (
          <div key={row.id} className="budget-row">
            <div className="budget-row-main">
              <div className="budget-row-name">{row.name}</div>
              <div className="budget-row-meta">
                Spent {formatCurrency(row.actual)} • Remaining {formatCurrency(row.remaining)}
              </div>
              <div className="budget-row-bar">
                <span style={{ width: `${row.progress}%` }} />
              </div>
            </div>
            <div className="budget-row-input">
              <label htmlFor={`budget-${row.id}`}>Budget</label>
              <input
                id={`budget-${row.id}`}
                className="input"
                type="number"
                min="0"
                step="0.01"
                value={row.budget || ""}
                onChange={(e) => updateBudget(row.id, e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BudgetMetric({ label, value, tone = "default" }) {
  return (
    <div className={`budget-metric budget-metric-${tone}`}>
      <div className="budget-metric-label">{label}</div>
      <div className="budget-metric-value">{value}</div>
    </div>
  );
}
